import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Navbar,
  Icon,
  ModeToggleSwitch,
  Container,
  Button,
  Text,
  LinkTapSiteModal,
  LinkTapSiteFormData,
  FormPreviewPanel,
  IfHasPermission,
  Modal,
} from '@ui-library';
import { ManageCollaborationModal } from '../components/ProjectModals';
import { IconName } from '@ui-library/Icon/Icon.type';
import { ChatMode } from '@ui-library/ModeToggleSwitch/ModeToggleSwitch.types';


import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatCostValue } from '../utils/formatCost';
import { formatLatency } from '../utils/formatLatency';
import { showDuration, canShowCost } from '../utils/usageConfig';
import { saveProjectPosition, getProjectPosition } from '../utils/projectNavigationStorage';
import { FedRampProject, PlatformType } from '../types/project';
import { DataModelObject } from '../types/dataModel';
import { DataModelApiService } from '../services/dataModelApi';
import { getPlatformConfig, getPlatformDisplayName } from '../config/platformConfig';
import DesignAgentChat from '../components/DesignAgentChat';
import DesignAgentPreview from '../components/DesignAgentPreview';
import DataModelPreview, { DataModelPreviewRef } from '../components/DataModelPreview';
import { FedRampBuildChat } from '../components/FedRampBuildChat';
import { Permissions } from '../constants';
import CABPage from './CABPage';
import TicketsPage from './TicketsPage';

// Skeleton Loading Component for Chat Messages
const ChatMessageSkeleton: React.FC<{ index: number; type: 'user' | 'assistant' }> = ({ index, type }) => {
  return (
    <div
      className={`flex ${type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up opacity-0`}
      style={{
        animationDelay: `${index * 0.15}s`,
        animationFillMode: 'forwards'
      }}
    >
      {/* Always keep avatar on left, message on right */}
      <div className="flex gap-bg-v2-md flex-row w-[60%]">
        {/* Avatar Skeleton - Always on left */}
        <div className="w-8 h-8 rounded-full skeleton-shimmer flex-shrink-0"></div>

        {/* Message Content Skeleton */}
        <div className="flex flex-col gap-bg-v2-sm flex-1">
          {/* Message Lines - No box, just plain skeleton lines */}
          <div className="space-y-2">
            <div className="h-4 skeleton-shimmer rounded w-full"></div>
            <div className="h-4 skeleton-shimmer rounded w-full"></div>
            <div className="h-4 skeleton-shimmer rounded w-[95%]"></div>
            <div className="h-4 skeleton-shimmer rounded w-[90%]"></div>
          </div>

          {/* Timestamp Skeleton - Always on left */}
          <div className="h-3 skeleton-shimmer rounded w-32 self-start"></div>
        </div>
      </div>
    </div>
  );
};

export const FedRampChatPage: React.FC = () => {
  const { projectId, type } = useParams<{ projectId: string, type: string }>();

  const [currentProject, setCurrentProject] = useState<FedRampProject | null>(null);
  const [projectCost, setProjectCost] = useState<number>(0);
  const [projectDuration, setProjectDuration] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [showLinkTapSiteModal, setShowLinkTapSiteModal] = useState(false);
  const chatMode = ChatMode[type?.toUpperCase() as keyof typeof ChatMode] || ChatMode.CAB;
  const [mode, setMode] = useState<ChatMode>(chatMode);
  // Track current mode via ref so async callbacks always see the latest value (not stale closures)
  const modeRef = useRef<ChatMode>(chatMode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  const [designPreviewRefresh, setDesignPreviewRefresh] = useState<boolean>(false);
  const [loadingDesignChat, setLoadingDesignChat] = useState(true);
  const [loadingBuildChat, setLoadingBuildChat] = useState(true);

  // Panel visibility states
  const [showDesignPreviewPanel, setShowDesignPreviewPanel] = useState(chatMode === ChatMode.DESIGN);
  const [showDataModelPreviewPanel, setShowDataModelPreviewPanel] = useState(false);
  const [showFormPreviewPanel, setShowFormPreviewPanel] = useState(false);
  const [showManageCollaborationModal, setShowManageCollaborationModal] = useState(false);
  const [showNotLinkedTapSiteModal, setShowNotLinkedTapSiteModal] = useState(false);

  // Panel closing animation states
  const [isDesignPreviewPanelClosing, setIsDesignPreviewPanelClosing] = useState(false);
  const [isDataModelPreviewPanelClosing, setIsDataModelPreviewPanelClosing] = useState(false);
  const [isFormPreviewPanelClosing, setIsFormPreviewPanelClosing] = useState(false);

  // Selected form business key for FormPreviewPanel
  const [selectedFormBusinessKey, setSelectedFormBusinessKey] = useState<string | undefined>(undefined);

  // DataModel state
  const [dataModel, setDataModel] = useState<DataModelObject[]>([]);
  const [dataModelLoading, setDataModelLoading] = useState(false);
  const [isDataModelEditing, setIsDataModelEditing] = useState(false);
  const dataModelPreviewRef = useRef<DataModelPreviewRef>(null);

  // CAB-to-Design transition state
  const [shouldAutoStartDesign, setShouldAutoStartDesign] = useState<boolean>(false);

  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { user, hasAdminRole } = useAuth();
  const showCost = canShowCost(hasAdminRole);

  const loadProject = async (id: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${id}`);
      setCurrentProject(response.data);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectCost = async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/projects/${projectId}/stats`);
      setProjectCost(parseFloat(response.data.total_cost) || 0);
      setProjectDuration(parseInt(response.data.total_duration_ms) || 0);
    } catch (err) {
      console.error('Failed to fetch project cost:', err);
      setProjectCost(0);
      setProjectDuration(0);
    }
  };

  // Fetch data model for DataModelPreview
  const fetchDataModel = useCallback(async () => {
    if (!projectId) return;

    try {
      setDataModelLoading(true);
      const result = await DataModelApiService.getDataModelPreview(projectId);

      let extractedDataModel: DataModelObject[] = [];

      if (result.exists && result.data_model) {
        // Extract data_objects if it's in the nested format
        if (result.data_model.data_objects) {
          extractedDataModel = result.data_model.data_objects;
        } else if (Array.isArray(result.data_model)) {
          extractedDataModel = result.data_model;
        }
        console.log('Loaded data model, objects:', extractedDataModel.length);
      }

      setDataModel(extractedDataModel);
    } catch (error) {
      console.error('Failed to fetch data model:', error);
      setDataModel([]);
    } finally {
      setDataModelLoading(false);
    }
  }, [projectId]);

  // Panel close handlers with animation
  const handleCloseDesignPreviewPanel = () => {
    setIsDesignPreviewPanelClosing(true);
    setTimeout(() => {
      setShowDesignPreviewPanel(false);
      setIsDesignPreviewPanelClosing(false);
    }, 250);
  };

  const handleCloseDataModelPreviewPanel = () => {
    setIsDataModelPreviewPanelClosing(true);
    setTimeout(() => {
      setShowDataModelPreviewPanel(false);
      setIsDataModelPreviewPanelClosing(false);
    }, 250);
  };

  const handleCloseFormPreviewPanel = () => {
    setIsFormPreviewPanelClosing(true);
    setTimeout(() => {
      setShowFormPreviewPanel(false);
      setIsFormPreviewPanelClosing(false);
    }, 250);
  };

  // Panel toggle handler
  const handlePanelToggle = (panelToToggle: 'designPreview' | 'dataModelPreview' | 'formPreview') => {
    const isPanelOpen =
      (panelToToggle === 'designPreview' && showDesignPreviewPanel) ||
      (panelToToggle === 'dataModelPreview' && showDataModelPreviewPanel) ||
      (panelToToggle === 'formPreview' && showFormPreviewPanel);

    if (isPanelOpen) {
      if (panelToToggle === 'designPreview') handleCloseDesignPreviewPanel();
      if (panelToToggle === 'dataModelPreview') handleCloseDataModelPreviewPanel();
      if (panelToToggle === 'formPreview') handleCloseFormPreviewPanel();
      return;
    }

    // Close other panel if open
    let otherPanelOpen = false;
    if (showDesignPreviewPanel) {
      handleCloseDesignPreviewPanel();
      otherPanelOpen = true;
    } else if (showDataModelPreviewPanel) {
      handleCloseDataModelPreviewPanel();
      otherPanelOpen = true;
    } else if (showFormPreviewPanel) {
      handleCloseFormPreviewPanel();
      otherPanelOpen = true;
    }

    const openTargetPanel = () => {
      if (panelToToggle === 'designPreview') {
        setShowDesignPreviewPanel(true);
      }
      if (panelToToggle === 'dataModelPreview') {
        fetchDataModel();
        setShowDataModelPreviewPanel(true);
      }
      if (panelToToggle === 'formPreview') {
        setShowFormPreviewPanel(true);
      }
    };

    if (otherPanelOpen) {
      setTimeout(openTargetPanel, 250);
    } else {
      openTargetPanel();
    }
  };

  // Handle data model changes from DataModelPreview
  const handleDataModelChange = (updatedDataModel: DataModelObject[]) => {
    setDataModel(updatedDataModel);
  };

  // Handle HTML generation from Design Agent
  const handleHtmlGenerated = (htmlPath: string) => {
    // Ignore if user has already switched away from Design mode
    if (modeRef.current !== ChatMode.DESIGN) return;

    console.log('handleHtmlGenerated called with path:', htmlPath);

    // Refresh project data (including cost) after message is sent
    if (projectId) {
      loadProject(projectId);
      fetchProjectCost();
      fetchDataModel(); // Refresh data model in case it was created/updated
    }

    // ServiceNow, PowerApps, and Salesforce: no HTML prototype — open Data Model Preview instead
    if (currentProject?.platform_type === PlatformType.ServiceNow || 
        currentProject?.platform_type === PlatformType.PowerApps ||
        currentProject?.platform_type === PlatformType.Salesforce) {
      setShowDesignPreviewPanel(false);
      setShowDataModelPreviewPanel(true);
      return;
    }

    // Trigger preview refresh
    setDesignPreviewRefresh(prev => {
      const newValue = !prev;
      console.log('Design preview refresh trigger updated:', prev, '->', newValue);
      return newValue;
    });
    // Auto-open Design Preview
    setShowDesignPreviewPanel(true);
    setShowDataModelPreviewPanel(false);
  };

  // Handle CAB-to-Design transition: switch to Design mode with pre-built prompt
  const handleGenerateDesignFromCAB = () => {
    setShouldAutoStartDesign(true);
    handleModeChange(ChatMode.DESIGN);
  };

  const handleModeChange = (newMode: ChatMode) => {
    if (!projectId) {
      return;
    }

    if (!currentProject?.tap_url && !currentProject?.permissions?.includes(Permissions.UpdateProjectPermission) && newMode === ChatMode.BUILD) {
      setShowNotLinkedTapSiteModal(true);
      return;
    };

    // Check if trying to switch to BUILD mode without TAP site linked
    if (newMode === ChatMode.BUILD && !currentProject?.tap_url) {
      setShowLinkTapSiteModal(true);
      return;
    }

    setMode(newMode);
    const savedPosition = getProjectPosition(projectId);
    
    if (newMode === ChatMode.CAB) {
      // Switching to CAB mode: close all preview panels
      setShowDesignPreviewPanel(false);
      setShowDataModelPreviewPanel(false);
      setShowFormPreviewPanel(false);
    } else if (newMode === ChatMode.TICKETS) {
      // Switching to Tickets mode: close all preview panels
      setShowDesignPreviewPanel(false);
      setShowDataModelPreviewPanel(false);
      setShowFormPreviewPanel(false);
    } else if (newMode === ChatMode.DESIGN) {
      // Switching to Design mode: show Design Preview, close Build mode panels
      setShowDesignPreviewPanel(true);
      setShowDataModelPreviewPanel(false);
      setShowFormPreviewPanel(false); // Close Form Preview (Build mode panel)
    } else {
      // Switching to Build mode: close Design mode panels
      setShowDesignPreviewPanel(false);
      setShowDataModelPreviewPanel(false);
      // Note: Form Preview stays closed until user opens it
    }

    saveProjectPosition(projectId, newMode as 'cab' | 'design' | 'build' | 'tickets', null);

    navigate(`/fedramp/chat/${projectId}/${newMode.toLowerCase()}`);
  };

  // Handle agent type detection from TAP Build Chat
  const handleAgentTypeDetected = (agentType: string | null) => {
    if (!agentType) {
      return;
    }

    // Ignore if user has already switched away from Build mode
    if (modeRef.current !== ChatMode.BUILD) return;

    // Check if agentType contains a business_key (format: "forms:business_key")
    if (agentType.startsWith('forms:')) {
      const businessKey = agentType.substring(6); // Extract business_key after "forms:"
      console.log('📋 [FedRampChatPage] Opening form panel with business_key:', businessKey);
      
      // Set the selected business key
      setSelectedFormBusinessKey(businessKey);
      
      // Close other panels if open
      if (showDesignPreviewPanel) {
        handleCloseDesignPreviewPanel();
      }
      if (showDataModelPreviewPanel) {
        handleCloseDataModelPreviewPanel();
      }
      
      // Open form preview panel after a short delay if another panel was closing
      const delay = (showDesignPreviewPanel || showDataModelPreviewPanel) ? 250 : 0;
      setTimeout(() => {
        setShowFormPreviewPanel(true);
      }, delay);
      return;
    }

    // Map agent types to panels
    switch (agentType) {
      case 'data_model':
        // Open Data Model Preview Panel (not toggle)
        if (!showDataModelPreviewPanel) {
          // Close other panels first
          if (showDesignPreviewPanel) {
            handleCloseDesignPreviewPanel();
          }
          if (showFormPreviewPanel) {
            handleCloseFormPreviewPanel();
          }
          
          const delay = (showDesignPreviewPanel || showFormPreviewPanel) ? 250 : 0;
          setTimeout(() => {
            fetchDataModel();
            setShowDataModelPreviewPanel(true);
          }, delay);
        }
        break;
      case 'forms':
        // Open Form Preview Panel (not toggle)
        if (!showFormPreviewPanel) {
          // Close other panels first
          if (showDesignPreviewPanel) {
            handleCloseDesignPreviewPanel();
          }
          if (showDataModelPreviewPanel) {
            handleCloseDataModelPreviewPanel();
          }
          
          const delay = (showDesignPreviewPanel || showDataModelPreviewPanel) ? 250 : 0;
          setTimeout(() => {
            setShowFormPreviewPanel(true);
          }, delay);
        }
        break;
      // Add more cases as needed for other agent types
      default:
        console.log('🔍 [FedRampChatPage] No panel mapping for agent type:', agentType);
    }
  };

  // Render mode-specific action buttons
  const renderModeActionButtons = () => {
    // CAB and Tickets modes: no action buttons
    if (mode === ChatMode.CAB || mode === ChatMode.TICKETS) {
      return null;
    }

    // Design mode: Link TAP Site, Design Preview, Data Model Preview
    if (mode === ChatMode.DESIGN) {
      return (
        <>
          <IfHasPermission
            permissions={currentProject?.permissions || []}
            requires={Permissions.UpdateProjectPermission}
          >
            <Button
              label={<Icon name={IconName.Link} size='sm' className='text-fg-secondary' inheritColor />}
              variant='ghost'
              size='small'
              title={getPlatformConfig(currentProject?.platform_type).linkButtonTitle}
              className='!rounded-full !p-bg-v2-lg'
              onClick={() => setShowLinkTapSiteModal(true)} />
          </IfHasPermission>
          {currentProject?.platform_type !== PlatformType.ServiceNow && 
           currentProject?.platform_type !== PlatformType.PowerApps &&
           currentProject?.platform_type !== PlatformType.Salesforce && (
            <Button
              label={<Icon name={IconName.Preview} size='sm' className={`${showDesignPreviewPanel ? 'text-border-brand-icn' : 'text-fg-secondary'}`} inheritColor />}
              variant='ghost'
              size='small'
              title='Prototype Preview'
              className={showDesignPreviewPanel ? '!bg-bg-brand-primary-alt !rounded-full !p-bg-v2-lg' : '!rounded-full !p-bg-v2-lg'}
              onClick={() => handlePanelToggle('designPreview')} />
          )}
          <Button
            label={<Icon name={IconName.DataModelPreview} size='sm' className={`${showDataModelPreviewPanel ? 'text-border-brand-icn' : 'text-fg-secondary'}`} inheritColor />}
            variant='ghost'
            size='small'
            title='Data Model Preview'
            className={showDataModelPreviewPanel ? '!bg-bg-brand-primary-alt !rounded-full !p-bg-v2-lg' : '!rounded-full !p-bg-v2-lg'}
            onClick={() => handlePanelToggle('dataModelPreview')} />
        </>
      );
    }

    // Build mode: Link TAP Site, Form Preview, Data Model Preview
    return (
      <>
        <IfHasPermission
          permissions={currentProject?.permissions || []}
          requires={Permissions.UpdateProjectPermission}
        >
          <Button
            label={<Icon name={IconName.Link} size='sm' className='text-fg-secondary' inheritColor />}
            variant='ghost'
            size='small'
            title={getPlatformConfig(currentProject?.platform_type).linkButtonTitle}
            className='!rounded-full !p-bg-v2-lg'
            onClick={() => setShowLinkTapSiteModal(true)} />
        </IfHasPermission>
        <Button
          label={<Icon name={IconName.Form} size='sm' className={`${showFormPreviewPanel ? 'text-border-brand-icn' : 'text-fg-secondary'}`} inheritColor />}
          variant='ghost'
          size='small'
          title='Form Preview'
          className={showFormPreviewPanel ? '!bg-bg-brand-primary-alt !rounded-full !p-bg-v2-lg' : '!rounded-full !p-bg-v2-lg'}
          onClick={() => handlePanelToggle('formPreview')} />
        <Button
          label={<Icon name={IconName.DataModelPreview} size='sm' className={`${showDataModelPreviewPanel ? 'text-border-brand-icn' : 'text-fg-secondary'}`} inheritColor />}
          variant='ghost'
          size='small'
          title='Data Model Preview'
          className={showDataModelPreviewPanel ? '!bg-bg-brand-primary-alt !rounded-full !p-bg-v2-lg' : '!rounded-full !p-bg-v2-lg'}
          onClick={() => handlePanelToggle('dataModelPreview')} />
      </>
    );
  };

  const handleLinkTapSite = async (linkTapSiteFormData: LinkTapSiteFormData) => {
    try {
      const formData = new FormData();
      const isServiceNow = currentProject?.platform_type === PlatformType.ServiceNow;
      const isPowerApps = currentProject?.platform_type === PlatformType.PowerApps;
      const isSalesforce = currentProject?.platform_type === PlatformType.Salesforce;

      if (isServiceNow) {
        formData.append('tap_url', linkTapSiteFormData.tapUrl ?? '');
        formData.append('servicenow_username', linkTapSiteFormData.servicenowUsername ?? '');
        formData.append('servicenow_password', linkTapSiteFormData.servicenowPassword ?? '');
      } else if (isPowerApps) {
        formData.append('tap_url', linkTapSiteFormData.tapUrl ?? '');
        formData.append('powerapps_tenant_id', linkTapSiteFormData.powerappsTenantId ?? '');
        formData.append('powerapps_client_id', linkTapSiteFormData.powerappsClientId ?? '');
        formData.append('powerapps_client_secret', linkTapSiteFormData.powerappsClientSecret ?? '');
      } else if (isSalesforce) {
        formData.append('tap_url', linkTapSiteFormData.tapUrl ?? '');
        formData.append('salesforce_client_id', linkTapSiteFormData.salesforceClientId ?? '');
        formData.append('salesforce_client_secret', linkTapSiteFormData.salesforceClientSecret ?? '');
      } else {
        formData.append('tap_url', linkTapSiteFormData.tapUrl ?? '');
        formData.append('app_id', linkTapSiteFormData.clientId ?? '');
        formData.append('app_secret', linkTapSiteFormData.clientSecret ?? '');
      }
      formData.append('migrate_data_model', linkTapSiteFormData.migrateDataModel ? 'true' : 'false');

      const response = await api.post(`/projects/${projectId}/link-tap-site`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: linkTapSiteFormData.migrateDataModel ? 900000 : 120000,
      });

      // Log response for debugging
      console.log('TAP site link response:', response.data);


      // Fetch fresh project data with all fields including permissions
      if (projectId) {
        await loadProject(projectId);
        // Only refresh data model if preview panel is already open
        if (showDataModelPreviewPanel) {
          await fetchDataModel();
        }
      }
      setMode(ChatMode.BUILD);
      if (showDesignPreviewPanel){
        setShowDesignPreviewPanel(false);
      }
      setShowLinkTapSiteModal(false);
    } catch (err: any) {
      console.error('Failed to link tap site:', err);
      throw err; // Re-throw to let modal handle the error
    }
  };

  // Redirect to /cab if type is missing
  useEffect(() => {
    if (projectId && !type) {
      navigate(`/fedramp/chat/${projectId}/cab`, { replace: true });
    }
  }, [projectId, type, navigate]);

  useEffect(() => {
    if (projectId) {
      const savedPosition = getProjectPosition(projectId);
      if (!savedPosition) {
        saveProjectPosition(projectId, type as 'cab' | 'design' | 'build' | 'tickets', null);
      }
      loadProject(projectId);
      fetchProjectCost();
      // Data model will be fetched only when needed (BUILD mode, Data Model Preview, etc.)
    }
  }, [projectId]);

  // ServiceNow projects: never show the Design (HTML) preview panel
  useEffect(() => {
    if ((currentProject?.platform_type === PlatformType.ServiceNow || 
         currentProject?.platform_type === PlatformType.PowerApps ||
         currentProject?.platform_type === PlatformType.Salesforce) && showDesignPreviewPanel) {
      setShowDesignPreviewPanel(false);
    }
  }, [currentProject?.platform_type]);

  useEffect(() => {
    if (currentProject && mode === ChatMode.BUILD && !currentProject.tap_url) {
      if (!currentProject?.permissions?.includes(Permissions.UpdateProjectPermission)) {
        setShowNotLinkedTapSiteModal(true);
        setMode(ChatMode.DESIGN);
        navigate(`/fedramp/chat/${projectId}/design`);
        return;
      }
      setShowLinkTapSiteModal(true);
      setMode(ChatMode.DESIGN);
      navigate(`/fedramp/chat/${projectId}/design`);
      return;
    }
  }, [currentProject, mode]);

  return (
    <div className='flex flex-col h-screen'>
      <Navbar breadcrumbs={[{ label: currentProject?.title || '' }]}
        user={user}
        projectCostDisplay={showCost ? `$${formatCostValue(projectCost)}` : undefined}
        projectDurationDisplay={showDuration ? formatLatency(projectDuration) : undefined}
        onHomeClick={() => {
          navigate('/dashboard');
        }}
        projectId={projectId}
        currentMode={mode === ChatMode.DESIGN ? 'design' : 'build'}
        actions={[
          {
            label: <Icon name={theme === 'dark' ? IconName.Sun : IconName.Moon} size='sm' variant='secondary' />,
            onClick: () => {
              setTheme(theme === 'dark' ? 'light' : 'dark');
            },
            tooltip: theme === 'dark' ? 'Light Mode' : 'Dark Mode',
          },
          {
            label: <Icon name={IconName.Bell} size='sm' variant='secondary' />,
            onClick: () => { },
            tooltip: 'Notifications',
          },
          {
            label: <Icon name={IconName.HelpCircle} size='sm' variant='secondary' />,
            onClick: () => {
              window.open('https://docs.bluegenai.com/', '_blank', 'noopener,noreferrer');
            },
            tooltip: 'Help',
          }
        ]}
      />
      <Container className='px-bg-v2-3xl flex items-end justify-between'>
        <ModeToggleSwitch
          activeMode={mode}
          onModeChange={handleModeChange}
          showTickets
        />
        <div className='gap-bg-v2-xs flex'>
          {renderModeActionButtons()}
          {/* Manage Collaboration - visible to the owner and collaborators with invite permission */}
          {currentProject?.permissions?.includes(Permissions.InviteUserPermission) && (
            <Button
              label={<Icon name={IconName.Users} size='sm' className={`${showManageCollaborationModal ? 'text-border-brand-icn' : 'text-fg-secondary'}`} inheritColor />}
              title='Manage Collaborators'
              variant='ghost'
              size='small'
              className='!rounded-full !p-bg-v2-lg'
              onClick={() => setShowManageCollaborationModal(true)}
            />
          )}
        </div>
      </Container>
      <Container variant='tertiary' className='flex-1 flex min-h-0 px-bg-v2-3xl py-bg-v2-xl gap-bg-v2-xl'>
         {/* CAB Mode */}
         {mode === ChatMode.CAB && projectId && (

            <CABPage
              permissions={currentProject?.permissions}
              platformType={currentProject?.platform_type}
              onGenerateDesign={handleGenerateDesignFromCAB}
              onProjectCostUpdate={() => { if (projectId) { loadProject(projectId); fetchProjectCost(); } }}
              onNavigateToTickets={() => handleModeChange(ChatMode.TICKETS)}
            />
          )}

          {/* Tickets Mode */}
          {mode === ChatMode.TICKETS && (
            <TicketsPage
              permissions={currentProject?.permissions}
              currentUserId={currentProject?.user_id}
              projectPlatformType={currentProject?.platform_type}
              projectOwnerId={currentProject?.user_id}
              projectOwnerName={
                user && [user.firstName, user.lastName].filter(Boolean).join(' ') || user?.username
              }
              projectOwnerEmail={user?.email}
              onEvaluationComplete={fetchProjectCost}
            />
          )}

          {mode !== ChatMode.CAB && mode !== ChatMode.TICKETS && (
        <Container className='rounded-bg-v2-2xl container-shadow-md border border-border-secondary flex-1 flex flex-col min-h-0 overflow-hidden' padding='none'>
          
            <div className='flex items-center min-h-[60px] border-b border-border-primary px-bg-v2-xl py-bg-v2-xl'>
              <Text variant='text-sm' weight='semibold' className='flex items-center gap-bg-v2-sm'>
                <Icon name={IconName.Layout} size='sm' variant='secondary' />
                {mode === ChatMode.DESIGN ? 'Prototype Mode' : 'Build Mode'}
              </Text>
            </div>
         
          {/* Design Agent Chat */}
          {mode === ChatMode.DESIGN && currentProject?._id && (
            <>
              {loadingDesignChat && (
                <div className='flex-1 bg-bg-primary min-h-0 flex flex-col items-center py-bg-v2-md relative'>
                  <div className='flex flex-col gap-bg-v2-xl w-full max-w-[800px] overflow-y-auto modern-scrollbar relative px-bg-v2-xl'>
                    <ChatMessageSkeleton index={0} type="user" />
                    <ChatMessageSkeleton index={1} type="assistant" />
                    <ChatMessageSkeleton index={2} type="user" />
                    <ChatMessageSkeleton index={3} type="assistant" />
                  </div>
                </div>
              )}
              <DesignAgentChat
                projectId={currentProject?._id!}
                onHtmlGenerated={handleHtmlGenerated}
                project={currentProject}
                onLoadingChange={setLoadingDesignChat}
                onCostUpdate={(cost) => { setCurrentProject(prev => prev ? { ...prev, total_cost: cost } : null); fetchProjectCost(); }}
                shouldAutoStartDesign={shouldAutoStartDesign}
                onAutoStartConsumed={() => setShouldAutoStartDesign(false)}
              />
            </>
          )}
          {/* Build Mode Chat */}
          {mode === ChatMode.BUILD && currentProject && (
            <>
              {loadingBuildChat && (
                <div className='flex-1 bg-bg-primary min-h-0 flex flex-col items-center py-bg-v2-md relative'>
                  <div className='flex flex-col gap-bg-v2-xl w-full max-w-[800px] overflow-y-auto modern-scrollbar relative px-bg-v2-xl'>
                    <ChatMessageSkeleton index={0} type="user" />
                    <ChatMessageSkeleton index={1} type="assistant" />
                    <ChatMessageSkeleton index={2} type="user" />
                    <ChatMessageSkeleton index={3} type="assistant" />
                  </div>
                </div>
              )}
              <FedRampBuildChat
                projectId={projectId || null}
                project={currentProject}
                onLoadingChange={setLoadingBuildChat}
                onCostUpdate={(cost) => { setCurrentProject(prev => prev ? { ...prev, total_cost: cost } : null); fetchProjectCost(); }}
                onAgentTypeDetected={handleAgentTypeDetected}
              />
            </>
          )}
        </Container>
          )}

        {/* Design Preview Panel */}
        {showDesignPreviewPanel && projectId && (
          <div className={`w-[55%] flex-shrink-0 ${isDesignPreviewPanelClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}>
            <DesignAgentPreview
              projectId={projectId}
              refreshTrigger={designPreviewRefresh}
              project={currentProject}
              onClose={handleCloseDesignPreviewPanel}
            />
          </div>
        )}

        {/* Data Model Preview Panel */}
        {showDataModelPreviewPanel && projectId && (
          <div className={`w-[55%] flex-shrink-0 ${isDataModelPreviewPanelClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}>
            <Container className='rounded-bg-v2-2xl container-shadow-md border border-border-secondary h-full flex flex-col overflow-hidden' padding='none'>
              {/* Panel Header */}
              <div className='flex items-center justify-between min-h-[60px] max-h-[60px] px-bg-v2-xl bg-bg-primary border-b border-border-secondary'>
                <div className='flex items-center gap-bg-v2-lg flex-1'>
                  <Icon name={IconName.Layout} size='sm' variant='secondary' />
                  <Text variant='text-sm' weight='semibold' color='primary'>
                    Data Model Preview
                  </Text>
                </div>
                <div className='flex items-center gap-bg-v2-sm'>
                  {isDataModelEditing && (
                    <IfHasPermission
                      permissions={currentProject?.permissions}
                      requires={Permissions.UpdateProjectPermission}
                    >
                      <>
                        <Button
                          onClick={() => dataModelPreviewRef.current?.handleSave()}
                          variant='ghost'
                          size='small'
                          className='!w-6 !h-6 !p-0 !rounded-full text-utility-green-600 hover:text-utility-green-700 hover:bg-utility-green-50 flex-shrink-0'
                          aria-label='Save'
                          title='Save Changes'
                          label={<Icon name={IconName.Check} size='sm' variant='success' />}
                        />
                        <Button
                          onClick={() => dataModelPreviewRef.current?.handleCancel()}
                          variant='ghost'
                          size='small'
                          className='!w-6 !h-6 !p-0 !rounded-full text-fg-secondary hover:text-fg-secondary-hover flex-shrink-0'
                          aria-label='Cancel'
                          title='Reset to Original'
                          label={<Icon name={IconName.Close} size='sm' variant='secondary' />}
                        />
                      </>
                    </IfHasPermission>
                  )}
                  {!currentProject?.tap_url && (
                    <Button
                      onClick={() => setIsDataModelEditing(!isDataModelEditing)}
                      variant='ghost'
                      size='small'
                      className='!w-6 !h-6 !p-0 !rounded-full text-fg-secondary hover:text-fg-secondary-hover flex-shrink-0'
                      aria-label={isDataModelEditing ? 'Switch to Visual View' : 'Edit'}
                      title={isDataModelEditing ? 'Switch to Visual View' : 'Edit'}
                      label={<Icon name={isDataModelEditing ? IconName.EyeOpen : IconName.Edit} size='sm' variant='secondary' />}
                    />
                  )}
                  <Button
                    onClick={handleCloseDataModelPreviewPanel}
                    variant='ghost'
                    size='small'
                    className='!w-6 !h-6 !p-0 !rounded-full text-fg-secondary hover:text-fg-secondary-hover flex-shrink-0'
                    aria-label='Close'
                    label={<Icon name={IconName.Cross} size='sm' inheritColor />}
                  />
                </div>
              </div>
              {/* Panel Content */}
              <div className='flex-1 min-h-0 overflow-auto p-bg-v2-xl'>
                <DataModelPreview
                  ref={dataModelPreviewRef}
                  projectId={projectId}
                  dataModel={dataModel}
                  onDataModelChange={handleDataModelChange}
                  isLoading={dataModelLoading}
                  project={currentProject}
                  isEditing={isDataModelEditing}
                  onEditingChange={setIsDataModelEditing}
                  isTapLinked={!!currentProject?.tap_url}
                />
              </div>
            </Container>
          </div>
        )}

        {/* Form Preview Panel */}
        {showFormPreviewPanel && projectId && currentProject && (
          <div className={`w-[55%] flex-shrink-0 ${isFormPreviewPanelClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}>
            <FormPreviewPanel
              project={currentProject}
              refreshTrigger={false}
              onClose={handleCloseFormPreviewPanel}
              selectedFormKey={selectedFormBusinessKey}
              onFormKeyChange={setSelectedFormBusinessKey}
            />
          </div>
        )}
      </Container>
      <LinkTapSiteModal
        isOpen={showLinkTapSiteModal}
        initialFormData={{
          tapUrl: currentProject?.tap_url || '',
          clientId: '',
          clientSecret: '',
          servicenowUsername: '',
          servicenowPassword: '',
        }}
        onClose={() => {
          setShowLinkTapSiteModal(false);
        }}
        onSubmit={handleLinkTapSite}
        projectId={projectId || ''}
        platformType={currentProject?.platform_type}
      />

      {/* Manage Collaboration Modal */}
      {showManageCollaborationModal && currentProject && (
        <ManageCollaborationModal
          isOpen={showManageCollaborationModal}
          onClose={() => setShowManageCollaborationModal(false)}
          selectedProject={{
            id: projectId || '',
            title: currentProject.title,
            description: currentProject.description || '',
            platform_type: currentProject.platform_type,
            project_type: currentProject.project_type,
            tap_url: currentProject.tap_url,
            app_secret: currentProject.app_secret,
            created_at: currentProject.created_at,
            user_id: currentProject.user_id,
          }}
        />
      )}
      <Modal
        isOpen={showNotLinkedTapSiteModal}
        title={`Site not linked`}
        onClose={() => setShowNotLinkedTapSiteModal(false)}
        onCancel={() => setShowNotLinkedTapSiteModal(false)}>
          <Text>
            Your {getPlatformDisplayName(currentProject?.platform_type)} site is not linked. Please link it to continue.
          </Text>
      </Modal>
    </div>
  );
};